import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    LineChart,
    Line,
    CartesianGrid,
} from 'recharts';
import { FaGlobe, FaBullhorn, FaStar, FaWalking, FaDollarSign } from 'react-icons/fa';
import { Card, CardContent } from '../../../shared/components/ui/Card';
import Loader from '../../../shared/components/ui/Loader';
import ActivityLog from '../components/ActivityLog';
import { createLogger } from '../../../shared/config/logger';
import { getActivityLog, getDashboardAnalytics } from '../services/dashboardApi';

const logger = createLogger('DashboardAnalyticsPage');

const toArray = (v) => (Array.isArray(v) ? v : []);

// X-axis key per chart type AND filter
const xKeyFor = (chart, filter) => {
    // Growth returns { month, value } on "total"
    if (chart === 'growth') {
        if (filter === 'total' || filter === 'year') {
            return 'month';
        }
        if (filter === 'month') {
            return 'week';
        }
        if (filter === 'week') {
            return 'day';
        }
        return 'label';
    }
    // Redemptions, Points, Engagement, MostVisited follow standard mapping
    if (filter === 'total') {
        return 'year';
    }
    if (filter === 'year') {
        return 'month';
    }
    if (filter === 'month') {
        return 'week';
    }
    if (filter === 'week') {
        return 'day';
    }
    return 'label';
};

export default function DashboardAnalytics() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState('total');
    const [loadingFiltered, setLoadingFiltered] = useState(true);
    const [loadingStatic, setLoadingStatic] = useState(true);

    const [stats, setStats] = useState({
        totalCustomers: 0,
        newCustomers: 0,
        scans: 0,
        redeemed: 0,
        pointsAwarded: 0,
        pointsRedeemed: 0,
        visits: 0,
        moneyEarned: 0,
        averageSpend: '0.00',
    });

    const [activityLogs, setActivityLogs] = useState([]);

    // Chart series
    const [seriesRedemptions, setSeriesRedemptions] = useState([]); // Reward Redemptions
    const [seriesPointsRedeemed, setSeriesPointsRedeemed] = useState([]); // Points Redeemed
    const [seriesGrowth, setSeriesGrowth] = useState([]); // Customer Growth
    const [seriesEngagement, setSeriesEngagement] = useState([]); // Engagement
    const [seriesMostVisited, setSeriesMostVisited] = useState([]); // Most Visited

    // Tables
    const [topCustomersPoints, setTopCustomersPoints] = useState([]);
    const [topCustomersVisits, setTopCustomersVisits] = useState([]);
    const [topCustomersSpending, setTopCustomersSpending] = useState([]);
    const [topMetric, setTopMetric] = useState('points'); // 'points' | 'visits' | 'spending'
    const [topRewards, setTopRewards] = useState([]);

    const [chartTitles, setChartTitles] = useState({
        redemptions: 'Reward Redemptions',
        points: 'Points Redeemed',
        growth: 'Customer Growth Over Time',
        engagement: 'Customer Engagement Over Time',
        mostVisited: 'Most Visited',
    });

    // ------- Fetch dashboard + activity whenever filter changes -------
    // Filter-dependent data (stats + series)
    useEffect(() => {
        const loadFiltered = async () => {
            setLoadingFiltered(true);
            try {
                const { data: payload } = await getDashboardAnalytics(filter);
                setStats(payload.stats || {});
                setSeriesRedemptions(toArray(payload?.series?.redemptions));
                setSeriesPointsRedeemed(toArray(payload?.series?.pointsRedeemed));
                setSeriesGrowth(toArray(payload?.series?.growth));
                setSeriesEngagement(toArray(payload?.series?.engagement));
                setSeriesMostVisited(toArray(payload?.series?.mostVisited));
                setChartTitles(payload.chartTitles || {});
            } catch (e) {
                logger.error('Dashboard stats load failed', { error: e, filter });
            } finally {
                setLoadingFiltered(false);
            }
        };
        loadFiltered();
    }, [filter]);

    // Static data (tables + logs)
    useEffect(() => {
        const loadStatic = async () => {
            setLoadingStatic(true);
            try {
                const { data: payload } = await getDashboardAnalytics('total'); // dummy filter just for reuse
                setTopCustomersPoints(toArray(payload.topCustomersByPoints));
                setTopCustomersVisits(toArray(payload.topCustomersByVisits));
                setTopCustomersSpending(toArray(payload.topCustomersBySpending));
                setTopRewards(toArray(payload.topRewards));

                const { data: act } = await getActivityLog(10);
                setActivityLogs(toArray(act?.logs));
            } catch (e) {
                logger.error('Dashboard static data load failed', { error: e });
            } finally {
                setLoadingStatic(false);
            }
        };
        loadStatic();
    }, []);

    // ------- Stat cards per selected filter -------
    const statCards = (() => {
        if (filter === 'total') {
            return [
                { label: 'Total Customers', value: stats.totalCustomers },
                { label: 'Reward Redemptions', value: stats.redeemed },
                { label: 'Points Awarded', value: stats.pointsAwarded },
                { label: 'Visits Total', value: stats.visits },
                { label: 'Avg Spend Per Visit ($)', value: stats.averageSpend },
            ];
        }
        const scope = filter.charAt(0).toUpperCase() + filter.slice(1);
        return [
            { label: 'New Customers', value: stats.newCustomers },
            { label: `Scans ${scope}`, value: stats.scans },
            { label: `Redeemed ${scope}`, value: stats.redeemed },
            { label: 'Points Awarded', value: stats.pointsAwarded },
            {
                label: 'Money Earned ($)',
                value:
                    typeof stats.moneyEarned === 'number'
                        ? stats.moneyEarned.toFixed(2)
                        : stats.moneyEarned,
            },
            { label: `Visits ${scope}`, value: stats.visits },
            { label: `Avg Spend Per Visit ${scope} ($)`, value: stats.averageSpend },
        ];
    })();

    // Which charts to render per spec
    const showCharts =
        filter === 'day'
            ? [] // none
            : filter === 'total'
              ? ['redemptions', 'points', 'growth'] // no engagement, no mostVisited
              : ['redemptions', 'points', 'growth', 'engagement', 'mostVisited'];

    // ------- NEW: zero-value filtering for "Top Customers" -------
    const filteredTopPoints = toArray(topCustomersPoints).filter((c) => (c?.points ?? 0) > 0);
    const filteredTopVisits = toArray(topCustomersVisits).filter((c) => (c?.visits ?? 0) > 0);
    const filteredTopSpending = toArray(topCustomersSpending).filter((c) => (c?.spending ?? 0) > 0);
    const currentTopCustomers =
        topMetric === 'points'
            ? filteredTopPoints
            : topMetric === 'visits'
              ? filteredTopVisits
              : filteredTopSpending;

    // Check if we have any data
    const hasData = () => {
        const hasStats = Object.values(stats).some((v) => {
            if (typeof v === 'number') {
                return v > 0;
            }
            if (typeof v === 'string') {
                return parseFloat(v) > 0;
            }
            return false;
        });
        const hasCharts =
            seriesRedemptions.length > 0 ||
            seriesPointsRedeemed.length > 0 ||
            seriesGrowth.length > 0 ||
            seriesEngagement.length > 0 ||
            seriesMostVisited.length > 0;
        const hasTables = currentTopCustomers.length > 0 || topRewards.length > 0;
        return hasStats || hasCharts || hasTables;
    };

    const isLoading = loadingFiltered || loadingStatic;
    const dataExists = hasData();
    const isInitialLoad = loadingStatic && !dataExists;

    // Show loader on initial load (when static data is loading and we have no data yet)
    if (isInitialLoad) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center min-h-[400px]">
                    <Loader className="h-16" />
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-10" data-tutorial="dashboard-overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4">
                <h1 className="text-3xl font-bold text-gray-800 md:row-start-1 md:col-start-1">Dashboard Overview</h1>
                <div className="flex flex-wrap gap-3 md:row-start-2 md:col-span-2">
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard/online-presence')}
                        className="inline-flex items-center justify-center gap-2 p-2 md:px-4 md:py-2 rounded-xl bg-primary text-white font-medium hover:bg-purple-600 transition shadow-sm"
                        title="Visibility Checker"
                    >
                        <FaGlobe className="text-lg shrink-0" />
                        <span className="hidden md:inline">Visibility Checker</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard/marketing')}
                        className="inline-flex items-center justify-center gap-2 p-2 md:px-4 md:py-2 rounded-xl bg-primary text-white font-medium hover:bg-purple-600 transition shadow-sm"
                        title="Marketing"
                    >
                        <FaBullhorn className="text-lg shrink-0" />
                        <span className="hidden md:inline">Marketing</span>
                    </button>
                </div>
                <div className="flex flex-wrap gap-2 p-2 md:p-0 md:row-start-1 md:col-start-2 md:items-center md:justify-end">
                    {['total', 'year', 'month', 'week', 'day'].map((option) => (
                        <button
                            key={option}
                            onClick={() => setFilter(option)}
                            className={`px-4 py-1 rounded-full text-sm font-medium border transition ${
                                filter === option
                                    ? 'bg-primary text-white'
                                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Show loader for filtered data when switching filters */}
            {loadingFiltered && !loadingStatic && (
                <div className="flex items-center justify-center py-8">
                    <Loader />
                </div>
            )}

            {/* Empty state message when no data */}
            {!isLoading && !dataExists && (
                <div className="flex flex-col items-center justify-center py-16">
                    <p className="text-lg text-gray-500 mb-2">No data available yet</p>
                    <p className="text-sm text-gray-400">
                        Start generating passes and tracking customer activity to see your dashboard data.
                    </p>
                </div>
            )}

            {/* Stat cards - hide if no data; 2 cols on mobile so cards share space */}
            {dataExists && !loadingFiltered && (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                    {statCards.map((card, idx) => (
                        <Card key={idx} className="shadow-sm">
                            <CardContent className="p-3 sm:p-4">
                                <p className="text-xs sm:text-sm text-gray-500 line-clamp-2">{card.label}</p>
                                <p className="text-lg sm:text-2xl font-semibold text-primary mt-1 truncate" title={String(card.value)}>{card.value}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Charts - hide if no data */}
            {showCharts.length > 0 && dataExists && !loadingFiltered && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Reward Redemptions */}
                    {showCharts.includes('redemptions') && seriesRedemptions.length > 0 && (
                        <Card>
                            <CardContent className="p-4">
                                <h2 className="font-bold mb-4">{chartTitles.redemptions}</h2>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart fill="#FF4DA6" data={toArray(seriesRedemptions)}>
                                        <XAxis dataKey={xKeyFor('redemptions', filter)} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Points Redeemed */}
                    {showCharts.includes('points') && seriesPointsRedeemed.length > 0 && (
                        <Card>
                            <CardContent className="p-4">
                                <h2 className="font-bold mb-4">{chartTitles.points}</h2>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart fill="#FF4DA6" data={toArray(seriesPointsRedeemed)}>
                                        <XAxis dataKey={xKeyFor('points', filter)} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Customer Growth Over Time */}
                    {showCharts.includes('growth') && seriesGrowth.length > 0 && (
                        <Card>
                            <CardContent className="p-4">
                                <h2 className="font-bold mb-4">{chartTitles.growth}</h2>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={toArray(seriesGrowth)}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey={xKeyFor('growth', filter)} />
                                        <YAxis />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            stroke="#00FFD0"
                                            dataKey="value"
                                            strokeWidth={2}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Customer Engagement Over Time */}
                    {showCharts.includes('engagement') && seriesEngagement.length > 0 && (
                        <Card>
                            <CardContent className="p-4">
                                <h2 className="font-bold mb-4">{chartTitles.engagement}</h2>
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart data={toArray(seriesEngagement)}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey={xKeyFor('engagement', filter)} />
                                        <YAxis />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            stroke="#00FFD0"
                                            dataKey="value"
                                            strokeWidth={2}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Most Visited (hidden on Total) */}
                    {showCharts.includes('mostVisited') && seriesMostVisited.length > 0 && (
                        <Card className="md:col-span-2">
                            <CardContent className="p-4">
                                <h2 className="font-bold mb-4">{chartTitles.mostVisited}</h2>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart fill="#FF4DA6" data={toArray(seriesMostVisited)}>
                                        <XAxis dataKey={xKeyFor('mostVisited', filter)} />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Tables - show even if empty, but hide if no data at all */}
            {dataExists && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                <h2 className="font-bold text-lg">Top Customers</h2>
                                <div className="flex gap-1 sm:gap-2">
                                    <button
                                        onClick={() => setTopMetric('points')}
                                        title="By Points"
                                        className={`p-2 sm:px-3 sm:py-1 rounded-full text-xs font-medium border transition inline-flex items-center justify-center gap-1.5 ${
                                            topMetric === 'points'
                                                ? 'bg-primary text-white border-primary'
                                                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        <FaStar className="w-4 h-4 sm:w-3.5 sm:h-3.5 shrink-0" />
                                        <span className="hidden sm:inline">By Points</span>
                                    </button>
                                    <button
                                        onClick={() => setTopMetric('visits')}
                                        title="By Visits"
                                        className={`p-2 sm:px-3 sm:py-1 rounded-full text-xs font-medium border transition inline-flex items-center justify-center gap-1.5 ${
                                            topMetric === 'visits'
                                                ? 'bg-primary text-white border-primary'
                                                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        <FaWalking className="w-4 h-4 sm:w-3.5 sm:h-3.5 shrink-0" />
                                        <span className="hidden sm:inline">By Visits</span>
                                    </button>
                                    <button
                                        onClick={() => setTopMetric('spending')}
                                        title="By Spending"
                                        className={`p-2 sm:px-3 sm:py-1 rounded-full text-xs font-medium border transition inline-flex items-center justify-center gap-1.5 ${
                                            topMetric === 'spending'
                                                ? 'bg-primary text-white border-primary'
                                                : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        <FaDollarSign className="w-4 h-4 sm:w-3.5 sm:h-3.5 shrink-0" />
                                        <span className="hidden sm:inline">By Spending</span>
                                    </button>
                                </div>
                            </div>

                            {loadingStatic ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader />
                                </div>
                            ) : currentTopCustomers.length === 0 ? (
                                <p className="text-sm text-gray-500">
                                    No customers yet with{' '}
                                    {topMetric === 'points'
                                        ? 'points'
                                        : topMetric === 'visits'
                                          ? 'visits'
                                          : 'spending'}{' '}
                                    &gt; 0.
                                </p>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {currentTopCustomers.map((c, i) => (
                                        <li key={i} className="py-2 flex justify-between text-sm">
                                            <span>{c.name}</span>
                                            {topMetric === 'points' ? (
                                                <span className="text-primary font-semibold">
                                                    {c.points} pts
                                                </span>
                                            ) : topMetric === 'visits' ? (
                                                <span className="text-primary font-semibold">
                                                    {c.visits} visits
                                                </span>
                                            ) : (
                                                <span className="text-primary font-semibold">
                                                    ${Number(c.spending).toFixed(2)}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-4">
                            <h2 className="font-bold text-lg mb-4">Most Redeemed Rewards</h2>
                            {loadingStatic ? (
                                <div className="flex items-center justify-center py-8">
                                    <Loader />
                                </div>
                            ) : topRewards.length === 0 ? (
                                <p className="text-sm text-gray-500">No rewards redeemed yet.</p>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {toArray(topRewards).map((r, i) => (
                                        <li key={i} className="py-2 flex justify-between text-sm">
                                            <span>{r.title}</span>
                                            <span className="text-primary font-semibold">
                                                {r.redemptions}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Activity Log */}
            {activityLogs.length > 0 && (
                <div className="grid gap-6">
                    <ActivityLog logs={activityLogs} />
                </div>
            )}
        </div>
    );
}
